import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CoverImg } from "@/components/ui/CoverImg";
import { IconBell } from "@/components/ui/icons";
import { api } from "@/lib/api";
import { currentSubscription, disablePush, enablePush, needsHomeScreen, pushSupported } from "@/lib/push";
import { sfx } from "@/lib/sfx";

type Item = {
  id: string; kind: "release"; label: string; album_id: string; title: string;
  artist: string; artist_id: string; cover: string | null; release_date: string; created_at: string;
};
type Feed = { items: Item[]; unread: number; push_key: string | null };

function ago(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return "aujourd'hui";
  if (days === 1) return "hier";
  return `il y a ${days} j`;
}

/** New-release alerts: bell with unread badge, list of fresh releases from
 *  the user's artists, and the switch for phone notifications (Web Push). */
export function NotificationsBell() {
  const nav = useNavigate();
  const [feed, setFeed] = useState<Feed | null>(null);
  const [open, setOpen] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMsg, setPushMsg] = useState<string | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(() => {
    api<Feed>("/api/notifications").then(setFeed).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = window.setInterval(load, 10 * 60 * 1000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    currentSubscription().then((s) => setPushOn(!!s)).catch(() => {});
    return () => { window.clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const on = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", on);
    window.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", on); window.removeEventListener("keydown", esc); };
  }, [open]);

  const toggleOpen = () => {
    sfx.tap();
    const next = !open;
    setOpen(next);
    setPushMsg(null);
    if (next && feed?.unread) {
      api("/api/notifications/read", { method: "POST" }).catch(() => {});
      setFeed({ ...feed, unread: 0 });
    }
  };

  const togglePush = async () => {
    if (!feed?.push_key) return;
    setPushBusy(true);
    try {
      if (pushOn) {
        await disablePush();
        setPushOn(false);
        setPushMsg("Notifications désactivées sur cet appareil.");
      } else {
        const r = await enablePush(feed.push_key);
        if (r === "on") { setPushOn(true); sfx.chirp(); setPushMsg("C'est activé ! Une notification de test vient d'être envoyée."); }
        else if (r === "denied") setPushMsg("Les notifications sont bloquées pour ce site dans ton navigateur.");
        else setPushMsg("Ton navigateur ne gère pas les notifications.");
      }
    } catch {
      setPushMsg("Impossible d'activer les notifications pour l'instant.");
    } finally { setPushBusy(false); }
  };

  const unread = feed?.unread ?? 0;
  return (
    <div ref={boxRef} style={{ position: "relative", flexShrink: 0 }}>
      <button className="btn ghost bell-btn" title="Nouveautés de tes artistes" onClick={toggleOpen}
              aria-label={unread ? `${unread} nouvelles sorties` : "Nouveautés"}>
        <IconBell />
        {unread > 0 && <span className="bell-badge">{unread > 9 ? "9+" : unread}</span>}
      </button>
      {open && (
        <div className="bell-panel" role="dialog" aria-label="Nouveautés">
          <div className="bell-head">
            <strong>Nouveautés de tes artistes</strong>
          </div>
          {feed?.push_key && pushSupported() && !needsHomeScreen() && (
            <button className={"bell-push" + (pushOn ? " on" : "")} onClick={() => void togglePush()} disabled={pushBusy}>
              {pushBusy ? "…" : pushOn ? "🔔 Notifications activées sur cet appareil · désactiver"
                                       : "🔔 Me prévenir sur cet appareil quand un artiste sort un son"}
            </button>
          )}
          {needsHomeScreen() && (
            <div className="bell-hint">
              Sur iPhone : ajoute slowson à l'écran d'accueil (Partager → « Sur l'écran d'accueil »)
              puis ouvre-le depuis là pour recevoir les notifications.
            </div>
          )}
          {pushMsg && <div className="bell-hint">{pushMsg}</div>}
          <div className="bell-list">
            {!feed?.items.length && (
              <div className="muted" style={{ padding: "18px 14px", fontSize: 13 }}>
                Rien de neuf pour l'instant. Dès qu'un artiste que tu écoutes sort un son, il apparaît ici.
              </div>
            )}
            {feed?.items.map((it) => (
              <button key={it.id} className="bell-item"
                      onClick={() => { sfx.tap(); setOpen(false); nav(`/album/${it.album_id}`); }}>
                <CoverImg coverId={it.cover} alt={it.title} size={52} />
                <span style={{ minWidth: 0, textAlign: "left" }}>
                  <span className="bell-label">{it.label} · {ago(it.release_date)}</span>
                  <span className="trunc" style={{ display: "block", fontWeight: 700 }}>{it.title}</span>
                  <span className="trunc muted" style={{ display: "block", fontSize: 13 }}>{it.artist}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
